import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Stack from "@mui/material/Stack";
import userApi, {
  getLatestArticleComment,
  getLatestArticleLike,
  getLatestProfileFeedMessage,
  getLatestReviewRecommendation,
  getLatestReviewComment,
  getLatestReviewLike,
  getLatestFriendArticle,
} from "../../HTTP/user";
import {
  getFriendsCount,
  getLatestFriendRequest,
  getLatestAcceptedFriend,
  getLastProfileViewer,
  getLatestFriendReview,
  getLatestFriendPublishedArticle,
  getLatestIgoFavorite,
  getMyShares,
  getViewsSummary,
  getProfileViewsSettings,
  setProfileViewsAnonymous,
} from "../../HTTP/stats";
import { goToStaticPage } from "../../Helper/utils";
import "./HomeHub.css";

const SHARE_NETWORKS = { x: "X", twitter: "X", link: "un lien", other: "un autre réseau", email: "e-mail" };
const formatNetwork = (network) =>
  SHARE_NETWORKS[network] || (network ? network.charAt(0).toUpperCase() + network.slice(1) : "un autre réseau");

const fullNameOf = (firstname, lastname, fallback = "Un ami") =>
  `${firstname || ""} ${lastname || ""}`.trim() || fallback;

const ACTIVITY_TYPES = {
  "article-comment": "Article",
  "article-like": "Article",
  "review-comment": "Revue de presse",
  "review-like": "Revue de presse",
  "profile-feed": "Profil",
  "copublication": "Copublication",
  "share": "Partage externe",
};

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

export default function HomeHub() {
  const navigate = useNavigate();
  const user = useMemo(() => safeJsonParse(localStorage.getItem("user")) || {}, []);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(false);
  const [reviewRecommendation, setReviewRecommendation] = useState(null);
  const [recommendationLoading, setRecommendationLoading] = useState(true);
  const [recommendationError, setRecommendationError] = useState(null);
  const [stats, setStats] = useState({
    profileToday: null,
    profileMonth: null,
    publicationToday: null,
    publicationMonth: null,
    friendsCount: null,
  });
  const [lastViewer, setLastViewer] = useState(undefined);
  const [friendReview, setFriendReview] = useState(null);
  const [friendPublishedArticle, setFriendPublishedArticle] = useState(null);
  const [networkLoading, setNetworkLoading] = useState(true);
  const [friendRequest, setFriendRequest] = useState(null);
  const [friendRequestBusy, setFriendRequestBusy] = useState(false);
  const [friendRequestResult, setFriendRequestResult] = useState(null);
  const [igoFavorite, setIgoFavorite] = useState(null);
  const [acceptedFriend, setAcceptedFriend] = useState(null);
  const [anonymous, setAnonymous] = useState(false);
  const [anonymousBusy, setAnonymousBusy] = useState(false);

  const fullName = `${user?.firstname || ""} ${user?.lastname || ""}`.trim() || "Utilisateur";

  useEffect(() => {
    let isMounted = true;

    const activityRequests = [
      { key: "article-comment", label: "Nouveau commentaire", icon: "💬", request: getLatestArticleComment },
      { key: "article-like", label: "Nouvelle mention J’aime", icon: "♥", request: getLatestArticleLike },
      { key: "review-comment", label: "Nouveau commentaire", icon: "💬", request: getLatestReviewComment },
      { key: "review-like", label: "Nouvelle mention J’aime", icon: "♥", request: getLatestReviewLike },
      { key: "profile-feed", label: "Nouveau message sur votre profil", icon: "✦", request: getLatestProfileFeedMessage },
      { key: "copublication", label: "Nouvelle copublication", icon: "🤝", request: getLatestFriendArticle },
      {
        key: "share",
        label: "Publication partagée",
        icon: "↗",
        request: async (id) => (await getMyShares(id, 1))?.latest?.[0] || null,
      },
    ];

    const loadActivities = async () => {
      if (!user?.id) {
        setActivitiesLoading(false);
        setRecommendationLoading(false);
        return;
      }

      const [results, recommendationResult] = await Promise.all([
        Promise.allSettled(activityRequests.map((activity) => activity.request(user.id))),
        getLatestReviewRecommendation(user.id)
          .then((data) => ({ status: "fulfilled", value: data }))
          .catch((error) => ({ status: "rejected", reason: error })),
      ]);

      if (!isMounted) return;

      const loadedActivities = results
        .map((result, index) => ({
          ...activityRequests[index],
          data: result.status === "fulfilled" ? result.value : null,
        }))
        .filter((activity) => activity.data);

      setActivities(loadedActivities);
      setActivitiesError(results.some((result) => result.status === "rejected"));
      if (recommendationResult.status === "fulfilled") {
        setReviewRecommendation(recommendationResult.value);
      } else {
        setRecommendationError(recommendationResult.reason?.status || "unknown");
      }
      setActivitiesLoading(false);
      setRecommendationLoading(false);
    };

    loadActivities();
    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setNetworkLoading(false);
      return undefined;
    }
    let isMounted = true;
    const valueOf = (result) => (result.status === "fulfilled" ? result.value : null);

    const loadHub = async () => {
      const [
        summary, friendsCount, viewer, settings, accepted,
        review, article, request, igo,
      ] = await Promise.allSettled([
        getViewsSummary(user.id),
        getFriendsCount(user.id),
        getLastProfileViewer(user.id),
        getProfileViewsSettings(user.id),
        getLatestAcceptedFriend(user.id),
        getLatestFriendReview(user.id),
        getLatestFriendPublishedArticle(user.id),
        getLatestFriendRequest(user.id),
        getLatestIgoFavorite(user.id),
      ]);
      if (!isMounted) return;

      const summaryValue = valueOf(summary);
      setStats({
        profileToday: summaryValue?.profile?.today ?? null,
        profileMonth: summaryValue?.profile?.month ?? null,
        publicationToday: summaryValue?.publications?.today ?? null,
        publicationMonth: summaryValue?.publications?.month ?? null,
        friendsCount: valueOf(friendsCount)?.count ?? null,
      });
      setAnonymous(Boolean(valueOf(settings)?.anonymous));
      setAcceptedFriend(valueOf(accepted)?.friend || null);
      if (viewer.status === "fulfilled") {
        setLastViewer(viewer.value?.viewer || null);
      } else {
        setLastViewer(viewer.reason?.status === 403 ? "locked" : undefined);
      }
      setFriendReview(valueOf(review)?.review || null);
      setFriendPublishedArticle(valueOf(article)?.article || null);
      setFriendRequest(valueOf(request)?.friend_request || null);
      setIgoFavorite(valueOf(igo)?.favorite || null);
      setNetworkLoading(false);
    };

    loadHub();
    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const toggleAnonymous = async () => {
    if (anonymousBusy) return;
    setAnonymousBusy(true);
    try {
      await setProfileViewsAnonymous(user.id, !anonymous);
      setAnonymous(!anonymous);
    } catch (error) {
      // l'état précédent est conservé
    } finally {
      setAnonymousBusy(false);
    }
  };

  const handleFriendRequest = async (accept) => {
    if (!friendRequest || friendRequestBusy) return;
    setFriendRequestBusy(true);
    try {
      if (accept) {
        await userApi.acceptFriendship(friendRequest.id);
      } else {
        await userApi.removeFriend(friendRequest.id);
      }
      setFriendRequestResult(accept ? "accepted" : "refused");
      setStats((current) => ({
        ...current,
        friendsCount: accept && current.friendsCount !== null ? current.friendsCount + 1 : current.friendsCount,
      }));
    } catch (error) {
      setFriendRequestResult("error");
    } finally {
      setFriendRequestBusy(false);
    }
  };

  const actions = [{ label: "Forfaits Zemus", path: "/zemus-plans" }];

  const getActivityValue = (activity, fields) => {
    for (const field of fields) {
      if (activity?.[field] !== undefined && activity?.[field] !== null && activity[field] !== "") {
        return activity[field];
      }
    }
    return "";
  };

  const formatActivityDate = (activity) => {
    const date = getActivityValue(activity, ["created_at", "createdAt", "date", "published_at", "updated_at"]);
    if (!date) return "";
    const parsedDate = new Date(date);
    return Number.isNaN(parsedDate.getTime()) ? String(date) : parsedDate.toLocaleString("fr-FR");
  };

  const getActivityDescription = (activity) =>
    getActivityValue(activity, ["content", "message", "comment", "text", "body", "description"])
    || "Une personne a aimé votre publication.";

  const getActivityDetails = ({ key, data }) => {
    if (key === "copublication") {
      const who = fullNameOf(data.friend_firstname, data.friend_lastname);
      return {
        title: data.review_theme || data.title || "",
        description: `${who} a copublié votre revue de presse.`,
        author: who,
        target: data.review_id ? { isArticle: false, id: data.review_id } : null,
      };
    }
    if (key === "share") {
      const who = fullNameOf(data.sharer_firstname, data.sharer_lastname, "Quelqu'un");
      const isArticle = data.entity_type === "article";
      const isReview = data.entity_type === "review";
      return {
        title: data.entity_title || "",
        description: `${who} a partagé votre publication sur ${formatNetwork(data.network)}.`,
        author: who,
        target: isArticle || isReview ? { isArticle, id: data.entity_id } : null,
      };
    }
    return {
      title: key.startsWith("review-")
        ? getActivityValue(data, ["review_theme"])
        : getActivityValue(data, ["title", "article_title", "publication_title"]),
      description: getActivityDescription(data),
      author: getActivityAuthor(data),
      target: getActivityTarget(data),
    };
  };

  const getActivityAuthor = (activity) => {
    const author = activity?.user || activity?.author || activity?.from_user || activity?.created_by;
    if (typeof author === "string") return author;
    return getActivityValue(author, ["name", "username", "full_name", "firstname"])
      || getActivityValue(activity, ["user_name", "author_name", "username"]);
  };

  const getActivityTarget = (activity) => {
    const articleId = getActivityValue(activity, ["article_id", "articleId"]);
    if (articleId) return { isArticle: true, id: articleId };

    const reviewId = getActivityValue(activity, ["review_id", "reviewId"]);
    if (reviewId) return { isArticle: false, id: reviewId };

    return null;
  };

  const getRecommendationErrorMessage = () => {
    if (recommendationError === 401) return "Votre session n'est plus valide.";
    if (recommendationError === 404) return "La recommandation est introuvable.";
    if (recommendationError === 500) return "La recommandation n'a pas pu être chargée.";
    return "La recommandation n'a pas pu être chargée.";
  };

  const recommendationReviewId = getActivityValue(reviewRecommendation, ["review_id"]);
  const formatCount = (value) => (value === null || value === undefined ? "—" : value);
  const recommendationDate = getActivityValue(reviewRecommendation, ["date"]);

  return (
    <div className="home-hub-wrapper">
      <Stack direction="row" spacing={2} id="stack_thing_in_header">
        <img
          className="iconProfile"
          src={user?.profile_picture || `${process.env.PUBLIC_URL}/profile_default.png`}
          alt="profil"
        />
        <div className="profilename">
          <h2 className="notranslate">{fullName}</h2>
        </div>
      </Stack>

      <section className="home-hub-actions" aria-label="Fonctionnalités principales">
        {actions.map((action) => (
          <button
            type="button"
            key={action.path}
            className="home-hub-action-btn"
            onClick={() => navigate(action.path)}
          >
            {action.label}
          </button>
        ))}
      </section>

      {/* <ForumLatestArticle /> */}

      <section className="home-hub-activity" aria-label="Votre visibilité">
        <div className="home-hub-activity-header">
          <div>
            <p className="home-hub-eyebrow">Votre visibilité</p>
            <h2>Votre activité en chiffres</h2>
          </div>
        </div>
        {networkLoading && <p className="home-hub-activity-state">Chargement de vos statistiques...</p>}
        {!networkLoading && (
          <div className="home-hub-activity-grid">
            <article className="home-hub-activity-card">
              <div className="home-hub-activity-icon" aria-hidden="true">👁</div>
              <div className="home-hub-activity-content">
                <h3>Vues du profil</h3>
                <p>Aujourd’hui : {formatCount(stats.profileToday)}</p>
                <p>Ce mois-ci : {formatCount(stats.profileMonth)}</p>
                {lastViewer === "locked" && (
                  <p className="home-hub-activity-state">
                    Passez Premium pour voir qui a consulté votre profil.{" "}
                    <button type="button" className="home-hub-activity-link" onClick={() => navigate("/zemus-plans")}>
                      Voir les forfaits
                    </button>
                  </p>
                )}
                {lastViewer && lastViewer !== "locked" && (
                  <p>
                    Dernier visiteur : {fullNameOf(lastViewer.firstname, lastViewer.lastname, "Visiteur")}
                    {lastViewer.viewed_at && <> — <time>{new Date(lastViewer.viewed_at).toLocaleString("fr-FR")}</time></>}
                  </p>
                )}
                <label className="home-hub-activity-state">
                  <input type="checkbox" checked={anonymous} disabled={anonymousBusy} onChange={toggleAnonymous} />{" "}
                  Ne pas être identifié lors de mes visites de profils
                </label>
              </div>
            </article>
            <article className="home-hub-activity-card">
              <div className="home-hub-activity-icon" aria-hidden="true">📈</div>
              <div className="home-hub-activity-content">
                <h3>Vues des publications</h3>
                <p>Aujourd’hui : {formatCount(stats.publicationToday)}</p>
                <p>Ce mois-ci : {formatCount(stats.publicationMonth)}</p>
              </div>
            </article>
            <article className="home-hub-activity-card">
              <div className="home-hub-activity-icon" aria-hidden="true">👥</div>
              <div className="home-hub-activity-content">
                <h3>Votre réseau</h3>
                <p>
                  {stats.friendsCount === null
                    ? "—"
                    : `${stats.friendsCount} ami${stats.friendsCount > 1 ? "s" : ""}`}
                </p>
              </div>
            </article>
          </div>
        )}
      </section>

      <section className="home-hub-activity" aria-label="Dernières activités">
        <div className="home-hub-activity-header">
          <div>
            <p className="home-hub-eyebrow">Autour de vous</p>
            <h2>Réactions à vos publications</h2>
          </div>
          <button type="button" onClick={() => navigate("/mypublications")} className="home-hub-activity-link">
            Mes publications
          </button>
        </div>

        {activitiesLoading && <p className="home-hub-activity-state">Chargement de vos dernières interactions...</p>}
        {!activitiesLoading && activitiesError && (
          <p className="home-hub-activity-state home-hub-activity-error">Certaines interactions n’ont pas pu être chargées.</p>
        )}
        {!activitiesLoading && activities.length === 0 && (
          <p className="home-hub-activity-state">Aucune interaction récente pour le moment.</p>
        )}
        <div className="home-hub-activity-grid">
          {activities.map((activity) => {
            const { title, description, author, target } = getActivityDetails(activity);
            return (
              <article
                key={activity.key}
                className={`home-hub-activity-card${target ? " home-hub-activity-card-clickable" : ""}`}
                onClick={target ? () => goToStaticPage(target.isArticle, target.id) : undefined}
                onKeyDown={target ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    goToStaticPage(target.isArticle, target.id);
                  }
                } : undefined}
                role={target ? "button" : undefined}
                tabIndex={target ? 0 : undefined}
              >
                <div className="home-hub-activity-icon" aria-hidden="true">{activity.icon}</div>
                <div className="home-hub-activity-content">
                  <h3>{activity.label}</h3>
                  {title && <p className="home-hub-activity-title">{title}</p>}
                  <p>{description}</p>
                  <div className="home-hub-activity-meta">
                    <span>{ACTIVITY_TYPES[activity.key]}</span>
                    {author && <span>{author}</span>}
                    {formatActivityDate(activity.data) && <time>{formatActivityDate(activity.data)}</time>}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="home-hub-activity" aria-label="Dernières publications de votre réseau">
        <div className="home-hub-activity-header">
          <div>
            <p className="home-hub-eyebrow">Dans votre réseau</p>
            <h2>Les dernières publications</h2>
          </div>
        </div>
        {networkLoading && <p className="home-hub-activity-state">Chargement des publications de votre réseau...</p>}
        {!networkLoading && !friendPublishedArticle && !friendReview && (
          <p className="home-hub-activity-state">Aucune publication récente de vos amis.</p>
        )}
        <div className="home-hub-activity-grid">
          {friendPublishedArticle && (
            <article
              className="home-hub-activity-card home-hub-activity-card-clickable"
              role="button"
              tabIndex={0}
              onClick={() => goToStaticPage(true, friendPublishedArticle.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  goToStaticPage(true, friendPublishedArticle.id);
                }
              }}
            >
              <div className="home-hub-activity-icon" aria-hidden="true">📰</div>
              <div className="home-hub-activity-content">
                <h3>Nouvel article</h3>
                <p>{fullNameOf(friendPublishedArticle.firstname, friendPublishedArticle.lastname)} a publié un article.</p>
                {friendPublishedArticle.title && <p className="home-hub-activity-title">{friendPublishedArticle.title}</p>}
                <div className="home-hub-activity-meta">
                  <span>Article</span>
                  {friendPublishedArticle.updated_at && <time>{formatActivityDate(friendPublishedArticle)}</time>}
                </div>
              </div>
            </article>
          )}
          {friendReview && (
            <article
              className="home-hub-activity-card home-hub-activity-card-clickable"
              role="button"
              tabIndex={0}
              onClick={() => goToStaticPage(false, friendReview.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  goToStaticPage(false, friendReview.id);
                }
              }}
            >
              <div className="home-hub-activity-icon" aria-hidden="true">🗞</div>
              <div className="home-hub-activity-content">
                <h3>Nouvelle republication</h3>
                <p>{fullNameOf(friendReview.firstname, friendReview.lastname)} a republié sa revue de presse.</p>
                {friendReview.theme && <p className="home-hub-activity-title">{friendReview.theme}</p>}
                <div className="home-hub-activity-meta">
                  <span>Revue de presse</span>
                  {friendReview.updated_at && <time>{formatActivityDate(friendReview)}</time>}
                </div>
              </div>
            </article>
          )}
        </div>
      </section>

      <section className="home-hub-activity" aria-label="Vos relations">
        <div className="home-hub-activity-header">
          <div>
            <p className="home-hub-eyebrow">Dans votre réseau</p>
            <h2>Vos relations</h2>
          </div>
        </div>
        {networkLoading && <p className="home-hub-activity-state">Chargement de vos relations...</p>}
        {!networkLoading && !friendRequest && !acceptedFriend && (
          <p className="home-hub-activity-state">Aucune nouvelle demande d’ami.</p>
        )}
        {acceptedFriend && (
          <article className="home-hub-activity-card">
            <div className="home-hub-activity-icon" aria-hidden="true">🤝</div>
            <div className="home-hub-activity-content">
              <h3>Demande d’ami acceptée</h3>
              <p>{fullNameOf(acceptedFriend.firstname, acceptedFriend.lastname, "Quelqu'un")} a accepté votre demande d’ami.</p>
              <div className="home-hub-activity-meta">
                {acceptedFriend.accepted_at && <time>{new Date(acceptedFriend.accepted_at).toLocaleString("fr-FR")}</time>}
              </div>
            </div>
          </article>
        )}
        {friendRequest && (
          <article className="home-hub-activity-card">
            <div className="home-hub-activity-icon" aria-hidden="true">👋</div>
            <div className="home-hub-activity-content">
              <h3>Nouvelle demande d’ami</h3>
              {!friendRequestResult && (
                <p>{fullNameOf(friendRequest.firstname, friendRequest.lastname, "Quelqu'un")} souhaite devenir votre ami(e).</p>
              )}
              {friendRequestResult === "accepted" && (
                <p>Vous avez accepté la demande de {fullNameOf(friendRequest.firstname, friendRequest.lastname)}.</p>
              )}
              {friendRequestResult === "refused" && <p>Demande refusée.</p>}
              {friendRequestResult === "error" && (
                <p className="home-hub-activity-error">L’action a échoué, veuillez réessayer.</p>
              )}
              <div className="home-hub-activity-meta">
                {friendRequest.requested_at && <time>{new Date(friendRequest.requested_at).toLocaleString("fr-FR")}</time>}
                {(!friendRequestResult || friendRequestResult === "error") && (
                  <>
                    <button type="button" disabled={friendRequestBusy} onClick={() => handleFriendRequest(true)}>
                      Accepter
                    </button>
                    <button type="button" disabled={friendRequestBusy} onClick={() => handleFriendRequest(false)}>
                      Refuser
                    </button>
                  </>
                )}
              </div>
            </div>
          </article>
        )}
      </section>

      <section className="home-hub-recommendation" aria-label="Recommandation de revue">
        <div className="home-hub-recommendation-header">
          <div>
            <p className="home-hub-eyebrow">À découvrir</p>
            <h2>Recommandé pour vous</h2>
          </div>
          {reviewRecommendation?.seen !== undefined && (
            <span className="home-hub-recommendation-seen">
              {reviewRecommendation.seen ? "Déjà consultée" : "À découvrir"}
            </span>
          )}
        </div>

        {recommendationLoading && <p className="home-hub-activity-state">Chargement de la recommandation...</p>}
        {!recommendationLoading && recommendationError && (
          <p className="home-hub-activity-state home-hub-activity-error">{getRecommendationErrorMessage()}</p>
        )}
        {!recommendationLoading && !recommendationError && !reviewRecommendation && (
          <p className="home-hub-activity-state">Aucune recommandation pour le moment.</p>
        )}
        {!recommendationLoading && !recommendationError && reviewRecommendation && (
          <article className="home-hub-recommendation-card">
            <div className="home-hub-recommendation-icon" aria-hidden="true">★</div>
            <div className="home-hub-recommendation-content">
              <p>{reviewRecommendation.message || "Une revue de presse vous a été recommandée."}</p>
              <div className="home-hub-activity-meta">
                {recommendationDate && <time>{formatActivityDate(reviewRecommendation)}</time>}
                {recommendationReviewId && (
                  <button type="button" onClick={() => goToStaticPage(false, recommendationReviewId)}>
                    Ouvrir la revue
                  </button>
                )}
              </div>
            </div>
          </article>
        )}
      </section>

      <section className="home-hub-friend-article" aria-label="Suggestions">
        <div className="home-hub-friend-article-header">
          <div>
            <p className="home-hub-eyebrow">À découvrir</p>
            <h2>Suggestions</h2>
          </div>
        </div>

        {networkLoading && <p className="home-hub-activity-state">Chargement des suggestions...</p>}
        {!networkLoading && !igoFavorite && (
          <p className="home-hub-activity-state">De nouvelles suggestions bientôt à découvrir.</p>
        )}
        {igoFavorite && (
          <article className="home-hub-friend-article-card">
            {igoFavorite.image && (
              <img className="home-hub-friend-article-image" src={igoFavorite.image} alt="" />
            )}
            <div className="home-hub-friend-article-content">
              <h3>{igoFavorite.title || "Article sans titre"}</h3>
              {igoFavorite.description && <p>{igoFavorite.description}</p>}
              <dl className="home-hub-friend-article-meta">
                {(igoFavorite.igo_label || igoFavorite.igo_name) && (
                  <><dt>IGO suivi</dt><dd>{igoFavorite.igo_label || igoFavorite.igo_name}</dd></>
                )}
                {igoFavorite.country && <><dt>Pays</dt><dd>{igoFavorite.country}</dd></>}
                {igoFavorite.user && (
                  <><dt>Mis en favori par</dt><dd>{fullNameOf(igoFavorite.user.firstname, igoFavorite.user.lastname)}</dd></>
                )}
                {igoFavorite.publication_date && (
                  <><dt>Publié le</dt><dd><time>{formatActivityDate({ date: igoFavorite.publication_date })}</time></dd></>
                )}
              </dl>
              {igoFavorite.link && (
                <a className="home-hub-friend-article-link" href={igoFavorite.link} target="_blank" rel="noopener noreferrer">
                  Ouvrir l'article
                </a>
              )}
            </div>
          </article>
        )}
      </section>
    </div>
  );
}
